import { useState, useEffect, lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { BookCallDialog } from "@/components/BookCallDialog";
import { PageTransition } from "@/components/PageTransition";
import { ScrollReveal, StaggerContainer, StaggerItem } from "@/components/ScrollReveal";
import { TypingAnimation } from "@/components/TypingAnimation";
import { HoverLift } from "@/components/MicroInteractions";
import { WaveDivider } from "@/components/PremiumEffects";
import { MessageSquare, ClipboardCheck, BarChart3, FileText, Users, Search, Calendar, Quote, ArrowRight, CheckCircle2, Sparkles, TrendingUp, Shield, ChevronDown, X, GraduationCap, AlertCircle, CalendarCheck } from "lucide-react";
import { EventParticipationDialog } from "@/components/forms/EventParticipationDialog";
// Hero image from public folder for LCP discoverability
const heroOffice = "/images/hero-office-tiny.webp";
import servicesBg from "@/assets/services-bg-tiny.webp";
import parkingLotBg from "@/assets/parking-lot-bg-tiny.webp";
import studentsClassroomBg from "@/assets/students-classroom-bg-tiny.webp";
import officeCollaborationBg from "@/assets/office-collaboration-bg-tiny.webp";
import teamCollaboration from "@/assets/team-collaboration-tiny.webp";
import businessHandshake from "@/assets/business-handshake-tiny.webp";

// Lazy load below-the-fold components to reduce initial JS payload
const ClientLogos = lazy(() => import("@/components/ClientLogos").then(m => ({ default: m.ClientLogos })));
const VideoTestimonials = lazy(() => import("@/components/VideoTestimonials").then(m => ({ default: m.VideoTestimonials })));
const GetInTouchForm = lazy(() => import("@/components/GetInTouchForm").then(m => ({ default: m.GetInTouchForm })));
const EventsGallery = lazy(() => import("@/components/EventsGallery").then(m => ({ default: m.EventsGallery })));

// Minimal loading fallback for lazy components
const SectionLoader = () => (
  <div className="flex items-center justify-center py-12">
    <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const rotatingTexts = [{
  title: "Process Documentation",
  description: "Clear SOPs and workflows that ensure consistency and make training a breeze."
}, {
  title: "Market Research",
  description: "Data-driven insights to inform your strategic decisions and identify opportunities."
}, {
  title: "Admin Support",
  description: "Virtual assistance to streamline your daily operations and save valuable time."
}, {
  title: "Business Planning",
  description: "Professional proposals and business plans that communicate your vision effectively."
}, {
  title: "Client Coordination",
  description: "Efficient management of appointments, meetings, and stakeholder communication."
}];

const features = [{
  icon: MessageSquare,
  title: "Reliable Communication",
  description: "Clear, timely updates keep you informed at every step."
}, {
  icon: ClipboardCheck,
  title: "Organized Execution",
  description: "Structured workflows ensure nothing falls through the cracks."
}, {
  icon: BarChart3,
  title: "Data-Driven Mindset",
  description: "Every decision backed by research and analysis."
}, {
  icon: FileText,
  title: "Professional Documentation",
  description: "Polished reports and SOPs that reflect your brand."
}];

const services = [{
  icon: Users,
  title: "Admin Support",
  description: "Virtual assistance to streamline your daily operations.",
  details: {
    overview: "Comprehensive administrative support designed to free up your time and streamline your business operations. From managing emails and calendars to handling day-to-day tasks, I ensure everything runs smoothly.",
    features: ["Email management and correspondence", "Calendar organization and scheduling", "Data entry and database management", "Document preparation and formatting", "Travel arrangements and logistics", "Meeting coordination and minutes"],
    benefits: "Save up to 20 hours per week while maintaining professional standards across all your administrative tasks."
  }
}, {
  icon: FileText,
  title: "Business Documentation",
  description: "Professional SOPs, proposals, and business plans.",
  details: {
    overview: "Transform your business processes into clear, professional documentation. From standard operating procedures to comprehensive business plans, I create documents that communicate your vision effectively.",
    features: ["Standard Operating Procedures (SOPs)", "Business plans and proposals", "Policy and procedure manuals", "Training materials and guides", "Reports and presentations", "Contract templates and agreements"],
    benefits: "Professional documentation that elevates your brand image and ensures consistency across all operations."
  }
}, {
  icon: Search,
  title: "Market & Competitor Research",
  description: "Actionable insights to inform your strategy.",
  details: {
    overview: "In-depth market analysis and competitive intelligence to help you make informed business decisions. I provide comprehensive research that uncovers opportunities and identifies potential threats.",
    features: ["Competitor analysis and benchmarking", "Market trend identification", "Customer demographic research", "Industry report compilation", "SWOT analysis development", "Pricing strategy research"],
    benefits: "Data-driven insights that give you a competitive edge and inform your strategic decisions."
  }
}, {
  icon: Calendar,
  title: "Customer & Client Coordination",
  description: "Seamless scheduling and communication management.",
  details: {
    overview: "Expert coordination services that ensure your clients receive exceptional experiences. From initial contact to ongoing relationship management, I handle all aspects of customer communication.",
    features: ["Client onboarding and welcome processes", "Appointment scheduling and reminders", "Follow-up communication management", "Customer inquiry handling", "Feedback collection and analysis", "CRM management and updates"],
    benefits: "Enhanced customer satisfaction and loyalty through consistent, professional communication."
  }
}];

// Testimonials removed - will be populated from database when real testimonials are collected
// Stats removed - will be populated from database when real metrics are available

export default function Index() {
  const [expandedService, setExpandedService] = useState<string | null>(null);
  const [bookCallOpen, setBookCallOpen] = useState(false);
  const [currentTextIndex, setCurrentTextIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTextIndex(prev => (prev + 1) % rotatingTexts.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  return <>
    <BookCallDialog open={bookCallOpen} onOpenChange={setBookCallOpen} />
    <Layout>
      <PageTransition>
        {/* Hero Section */}
        <section id="hero" className="relative min-h-[100vh] flex items-center overflow-hidden">
          {/* Premium Gradient Background */}
          <div className="absolute inset-0 z-0 bg-gradient-to-br from-primary via-primary/95 to-primary/80" />
          
          {/* Hero Background Image */}
          <div className="absolute inset-0 z-[1]">
            <img 
              src={heroOffice} 
              alt="" 
              className="absolute inset-0 w-full h-full object-cover opacity-40"
              fetchPriority="high"
            />
          </div>
          
          {/* Premium mesh gradient overlay */}
          <div className="absolute inset-0 z-[2] bg-gradient-to-t from-primary via-transparent to-primary/50" />
          <div className="absolute inset-0 z-[3] bg-gradient-to-r from-primary/90 via-primary/40 to-transparent" />
          
          {/* Decorative elements */}
          <div className="absolute top-20 right-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl z-[4]" />
          <div className="absolute bottom-20 left-10 w-72 h-72 bg-accent/5 rounded-full blur-3xl z-[4]" />
          
          <div className="container mx-auto px-4 lg:px-8 relative z-20">
            <div className="max-w-4xl">
              {/* Welcome badge - fixed height to prevent CLS */}
              <div className="min-h-[44px] mb-8">
                <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/15 text-white text-sm font-semibold backdrop-blur-md border border-white/30 shadow-lg drop-shadow-lg">
                  <Sparkles className="h-4 w-4 text-accent" />
                  Welcome to Hervé K's Platform
                </span>
              </div>

              {/* Static Brand Title */}
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-[1.1] mb-3 tracking-tight drop-shadow-2xl [text-shadow:_0_4px_24px_rgb(0_0_0_/_40%)]">
                <span className="bg-gradient-to-r from-accent to-accent/80 bg-clip-text text-transparent drop-shadow-lg">VantageStrategic</span>
              </h1>
              
              {/* Tagline */}
              <p className="text-lg md:text-xl text-white/90 mb-2 font-medium tracking-wide drop-shadow-md [text-shadow:_0_2px_8px_rgb(0_0_0_/_25%)]">
                Excellence • Your Strategic Partner
              </p>
              
              <p className="text-base md:text-lg text-white/95 font-medium drop-shadow-lg [text-shadow:_0_2px_12px_rgb(0_0_0_/_30%)]">
                by <span className="text-accent font-bold drop-shadow-lg">Hervé K</span>
              </p>

              {/* Spacer */}
              <div className="h-16 md:h-24"></div>

              {/* Value Proposition */}
              <p className="text-base md:text-lg text-white/85 mb-8 max-w-2xl leading-relaxed drop-shadow-md [text-shadow:_0_2px_8px_rgb(0_0_0_/_25%)]">
                Empowering you through strategic planning, seamless client coordination, and data-driven insights to make informed decisions and identify growth opportunities.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-3">
                <Button variant="hero" size="default" className="shadow-accent text-sm px-6 py-2.5 font-semibold" asChild>
                  <Link to="/services">
                    View Services
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </Link>
                </Button>
                <Button variant="heroOutline" size="default" className="text-sm px-6 py-2.5 font-semibold border-2" asChild>
                  <a href="https://calendly.com" target="_blank" rel="noopener noreferrer">
                    Book a Call
                  </a>
                </Button>
                <EventParticipationDialog 
                  triggerVariant="hero"
                  triggerClassName="shadow-accent text-sm px-6 py-2.5 font-semibold bg-white/15 hover:bg-white/25 text-white border border-white/30"
                  showQrInTrigger={true}
                />
              </div>

              {/* Trusted By Banner */}
              <div className="mt-12">
                <div className="inline-flex items-center gap-3 px-6 py-3 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 shadow-lg">
                  <Shield className="h-5 w-5 text-accent" />
                  <span className="text-sm font-medium text-white">Trusted by Professionals Across Industries</span>
                </div>
              </div>
            </div>
          </div>

          {/* Scroll indicator - CSS animation only for performance */}
          <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-3">
            <span className="text-primary-foreground/50 text-xs uppercase tracking-widest font-medium">Scroll</span>
            <div className="w-6 h-10 rounded-full border-2 border-accent/50 flex items-start justify-center p-2">
              <div className="w-1.5 h-1.5 rounded-full bg-accent animate-bounce" />
            </div>
          </div>
        </section>

        {/* Wave Divider */}
        <WaveDivider className="-mt-1 relative z-10" color="text-muted/30" />

        {/* Why Work With Me - Premium Redesigned */}
        <section id="why-choose" className="py-16 lg:py-24 bg-gradient-subtle relative overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-accent/3 rounded-full blur-3xl" />
          
          <div className="container mx-auto px-4 lg:px-8 relative">
            {/* Header with Image */}
            <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
              <ScrollReveal>
                <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-accent/10 text-accent text-sm font-semibold mb-6 border border-accent/20">
                  <TrendingUp className="h-4 w-4" />
                  Why Choose Me
                </span>
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-4 tracking-tight">
                  The Advantage You Get
                </h2>
                <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
                  Professional support backed by dedication, skill, and a commitment to your success. Join a growing community of satisfied clients.
                </p>
              </ScrollReveal>
              
              <div className="relative hidden lg:block animate-fade-up" style={{ animationDelay: '0.2s' }}>
                <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                  <img 
                    src={teamCollaboration} 
                    alt="Professional team collaboration" 
                    className="w-full h-72 object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/40 to-transparent" />
                </div>
                {/* Floating badge */}
                <div className="absolute -bottom-4 -left-4 bg-card rounded-2xl p-4 shadow-xl border border-border">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent to-accent/80 flex items-center justify-center">
                      <CheckCircle2 className="h-6 w-6 text-accent-foreground" />
                    </div>
                    <div>
                      <p className="font-bold text-foreground">Quality</p>
                      <p className="text-xs text-muted-foreground">Assured</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
              {features.map((feature, index) => <StaggerItem key={feature.title}>
                <div className="group relative p-6 lg:p-8 rounded-2xl overflow-hidden h-full bg-card border border-border hover:border-accent/40 hover:shadow-xl transition-all duration-500">
                  {/* Premium gradient background on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-accent/10 via-accent/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  {/* Accent line */}
                  <div className="absolute top-0 left-6 right-6 h-0.5 bg-gradient-to-r from-transparent via-accent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  <div className="relative">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent to-accent/80 flex items-center justify-center mb-5 shadow-accent group-hover:scale-110 transition-transform duration-300">
                      <feature.icon className="h-6 w-6 text-accent-foreground" />
                    </div>
                    <h3 className="text-lg font-bold text-foreground mb-3 group-hover:text-accent transition-colors duration-300">
                      {feature.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed text-sm">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </StaggerItem>)}
            </StaggerContainer>
          </div>
        </section>

        {/* Services Snapshot - Premium */}
        <section id="services" className="py-16 lg:py-24 relative overflow-hidden">
          {/* Premium Background */}
          <div className="absolute inset-0">
            <img src={servicesBg} alt="" className="w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-br from-primary/90 via-primary/80 to-primary/70" />
          </div>
          
          <div className="container mx-auto px-4 lg:px-8 relative z-20">
            <div className="text-center mb-12 animate-fade-up">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/20 text-accent text-xs font-semibold mb-4 backdrop-blur-md border border-accent/30">
                <Shield className="h-3.5 w-3.5" />
                What I Offer
              </span>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 tracking-tight">
                Services Snapshot
              </h2>
              <p className="text-white/80 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
                Comprehensive support tailored to your business needs.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {services.map((service, index) => (
                <HoverLift key={service.title} amount={-6}>
                  <div 
                    className="group relative bg-card rounded-2xl border border-border/50 hover:border-accent/40 hover:shadow-2xl transition-all duration-500 overflow-hidden h-full animate-fade-up"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    {/* Premium corner accent */}
                    <div className="absolute top-0 right-0 w-36 h-36 bg-gradient-to-br from-accent/15 to-transparent rounded-full -translate-y-1/2 translate-x-1/2 group-hover:scale-150 transition-transform duration-700" />
                    <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-accent/10 to-transparent rounded-full translate-y-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    
                    <div className="relative p-6 lg:p-8">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent to-accent/80 flex items-center justify-center mb-4 shadow-accent group-hover:scale-110 transition-transform duration-300">
                        <service.icon className="h-6 w-6 text-accent-foreground" />
                      </div>
                      <h3 className="text-xl font-bold text-foreground mb-3 group-hover:text-accent transition-colors duration-300">
                        {service.title}
                      </h3>
                      <p className="text-muted-foreground leading-relaxed text-sm">
                        {service.description}
                      </p>
                      <button onClick={() => setExpandedService(expandedService === service.title ? null : service.title)} className="inline-flex items-center gap-2 text-accent font-medium mt-3 text-sm hover:gap-3 transition-all">
                        {expandedService === service.title ? "Show less" : "Learn more"}
                        <ChevronDown className={`h-4 w-4 transition-transform duration-300 ${expandedService === service.title ? 'rotate-180' : ''}`} />
                      </button>
                    </div>

                    {/* Expanded Details - CSS transition instead of framer-motion */}
                    <div className={`overflow-hidden transition-all duration-300 ${expandedService === service.title ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                      <div className="px-8 pb-8 pt-2 border-t border-border/50">
                        <p className="text-foreground mb-4 leading-relaxed">
                          {service.details.overview}
                        </p>
                        
                        <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                          <CheckCircle2 className="h-5 w-5 text-accent" />
                          What's Included
                        </h4>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
                          {service.details.features.map((feature, idx) => <li key={idx} className="flex items-start gap-2 text-muted-foreground text-sm">
                              <CheckCircle2 className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" />
                              {feature}
                            </li>)}
                        </ul>
                        
                        <div className="p-4 bg-accent/10 rounded-xl border border-accent/20">
                          <p className="text-sm text-foreground font-medium">
                            <Sparkles className="h-4 w-4 text-accent inline mr-2" />
                            {service.details.benefits}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </HoverLift>
              ))}
            </div>

            <div className="text-center mt-12 animate-fade-up" style={{ animationDelay: '0.4s' }}>
              <Button variant="premium" size="lg" className="shadow-accent" asChild>
                <Link to="/services">
                  View All Services
                  <ArrowRight className="h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Wave Divider */}
        <WaveDivider flip className="-mb-1 relative z-10" color="text-muted/30" />

        {/* Client Logos */}
        <Suspense fallback={<SectionLoader />}>
          <ClientLogos />
        </Suspense>

        {/* Video Testimonials */}
        <Suspense fallback={<SectionLoader />}>
          <VideoTestimonials />
        </Suspense>

        {/* Events Gallery */}
        <Suspense fallback={<SectionLoader />}>
          <EventsGallery />
        </Suspense>

        {/* Quick Forms Section */}
        <section className="py-16 lg:py-20 bg-gradient-subtle relative overflow-hidden">
          <div className="absolute inset-0 pattern-dots opacity-30" />
          <div className="absolute top-0 right-0 w-72 h-72 bg-accent/5 rounded-full blur-3xl" />
          
          <div className="container mx-auto px-4 lg:px-8 relative z-10">
            <div className="text-center mb-10 animate-fade-up">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent/10 text-accent text-xs font-semibold mb-3">
                <FileText className="h-3.5 w-3.5" />
                Connect with Hervé K
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
                Quick Access Forms
              </h2>
              <p className="text-muted-foreground text-base max-w-2xl mx-auto">
                Join our community by registering for events, submitting inquiries, or partnering with us.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-4xl mx-auto">
              <div className="animate-fade-up" style={{ animationDelay: '0.1s' }}>
                <Link to="/forms" className="group block p-6 bg-card rounded-xl border border-border hover:border-accent/40 hover:shadow-xl transition-all text-center h-full">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform shadow-lg">
                    <Calendar className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-2 group-hover:text-accent transition-colors">
                    Event Registration
                  </h3>
                  <p className="text-muted-foreground text-sm mb-3">
                    Register for upcoming workshops, seminars, and networking events.
                  </p>
                  <span className="inline-flex items-center gap-2 text-accent font-medium text-sm">
                    Register Now <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </div>

              <div className="animate-fade-up" style={{ animationDelay: '0.2s' }}>
                <Link to="/contact" className="group block p-6 bg-card rounded-xl border border-border hover:border-accent/40 hover:shadow-xl transition-all text-center h-full">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-400 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform shadow-lg">
                    <MessageSquare className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-2 group-hover:text-accent transition-colors">
                    Get in Touch
                  </h3>
                  <p className="text-muted-foreground text-sm mb-3">
                    Send a message or inquiry about services and collaborations.
                  </p>
                  <span className="inline-flex items-center gap-2 text-accent font-medium text-sm">
                    Contact Us <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </div>

              <div className="animate-fade-up" style={{ animationDelay: '0.3s' }}>
                <Link to="/forms" className="group block p-6 bg-card rounded-xl border border-border hover:border-accent/40 hover:shadow-xl transition-all text-center h-full">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-400 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform shadow-lg">
                    <Users className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground mb-2 group-hover:text-accent transition-colors">
                    Join Our Network
                  </h3>
                  <p className="text-muted-foreground text-sm mb-3">
                    Explore partnership opportunities and collaboration requests.
                  </p>
                  <span className="inline-flex items-center gap-2 text-accent font-medium text-sm">
                    Explore Options <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials - Coming Soon */}
        <section id="testimonials" className="py-16 lg:py-20 relative overflow-hidden">
          {/* Static Background */}
          <div className="absolute inset-0">
            <img src={studentsClassroomBg} alt="" className="w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-primary/60" />
          </div>
          
          <div className="container mx-auto px-4 lg:px-8 relative z-20">
            <div className="text-center animate-fade-up">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent/20 text-accent text-xs font-semibold mb-3 backdrop-blur-sm border border-accent/30">
                <Quote className="h-3.5 w-3.5" />
                Testimonials
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
                What Clients Say
              </h2>
              <p className="text-white/90 text-base max-w-2xl mx-auto font-medium mb-8">
                Building trust through consistent, quality work.
              </p>
              <div className="inline-flex items-center gap-3 px-6 py-4 rounded-xl bg-white/10 backdrop-blur-md border border-white/20">
                <Shield className="h-5 w-5 text-accent" />
                <span className="text-sm text-white">Client testimonials coming soon</span>
              </div>
            </div>
          </div>
        </section>

        {/* Get In Touch Section */}
        <section id="contact" className="py-16 lg:py-20 bg-gradient-subtle relative overflow-hidden">
          <div className="absolute inset-0 pattern-dots opacity-30" />
          <div className="container mx-auto px-4 lg:px-8 relative">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div className="animate-fade-up">
                <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple/10 text-purple text-xs font-medium mb-3">
                  <MessageSquare className="h-3.5 w-3.5" />
                  Get In Touch
                </span>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
                  Write to Herve K.
                </h2>
                <p className="text-muted-foreground text-base mb-5">
                  Have a question or want to discuss a project? Send me a message and I will get back to you within 24 hours.
                </p>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-accent" />
                    <span className="text-foreground text-sm">Quick response time</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-accent" />
                    <span className="text-foreground text-sm">Free initial consultation</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-accent" />
                    <span className="text-foreground text-sm">No commitment required</span>
                  </div>
                </div>
              </div>

              <div className="bg-card p-6 rounded-xl border border-border shadow-lg animate-fade-up" style={{ animationDelay: '0.2s' }}>
                <Suspense fallback={<SectionLoader />}>
                  <GetInTouchForm />
                </Suspense>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 lg:py-20 relative overflow-hidden">
          <div className="container mx-auto px-4 lg:px-8">
            <div className="relative p-10 md:p-12 rounded-2xl text-center overflow-hidden animate-fade-up">
              {/* Background Image */}
              <div className="absolute inset-0 z-0 rounded-2xl overflow-hidden">
                <img src={parkingLotBg} alt="" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="absolute inset-0 bg-primary/50 z-10 rounded-2xl" />
              <div className="absolute inset-0 pattern-grid opacity-30 z-15" />
              <div className="absolute top-8 left-8 w-16 h-16 bg-accent/10 rounded-full blur-xl animate-float z-15" />
              <div className="absolute bottom-8 right-8 w-24 h-24 bg-accent/5 rounded-full blur-2xl animate-float z-15" style={{ animationDelay: '3s' }} />
              
              <div className="relative z-20">
                <h2 className="text-2xl md:text-3xl font-bold text-primary-foreground mb-3">
                  Ready to Get Started?
                </h2>
                <p className="text-primary-foreground/90 text-base max-w-xl mx-auto mb-6">
                  Let's discuss how I can help streamline your operations and support your business growth.
                </p>
                <div className="flex flex-wrap justify-center gap-3">
                  <Button size="default" className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-lg" onClick={() => setBookCallOpen(true)}>
                    Schedule a Call
                  </Button>
                  <Button variant="outline" size="default" className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10" asChild>
                    <Link to="/services">Browse Services</Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </PageTransition>
    </Layout>
  </>;
}
